// CascadeSelect — reusable two-column (code | label) cascading dropdowns.
// Domain-neutral (Component_Contract Tier 1). Theme tokens only; color-blind safe
// (a two-column list — numeric code left, label right — carries meaning by
// position + text, never color). Native <select> can't render two aligned columns,
// so this is a small CUSTOM dropdown: a button + a popover list with keyboard
// navigation and click-outside dismissal. The popover renders in a PORTAL on
// document.body (fixed-positioned from the button rect, flips up when short on
// room) so it is never clipped by a grid cell's overflow:hidden — found when the
// hierarchy grid's Parent cascade showed only 1-2 rows (Alex, 2026-07-02).
//
// Two exports:
//   • CascadeDropdown — ONE two-column dropdown (the atom). Reused for a single
//     level, the "item" picker, or the "Size" picker.
//   • CascadeSelect   — N CascadeDropdowns from a `levels` array (the cascade).
//     Each level's `options` are supplied by the consumer already filtered to the
//     previous pick — CascadeSelect holds NO catalog/domain logic (so the P5
//     reparent can reuse it levels-only).
//
// CascadeDropdown props:
//   value       — current option code (string | number | '' | null)
//   onChange    — (code) => void   (code of the chosen option, or '' for the placeholder)
//   options     — [{ code, label, hint?, display? }]  (code = the value passed to
//                 onChange; display = optional text for the mono code column when it
//                 should differ from the value, e.g. a size shown as "8" while the
//                 value is "size:8"; hint = optional right-aligned note, e.g. a price)
//   placeholder — text shown when nothing is selected (default '— any —')
//   disabled    — bool; renders inert (greyed, no popover)
//   allowClear  — bool; when true a leading "— any —" clear row is offered (default true)
//   ariaLabel   — accessible name for the button
//   width       — button width (px or CSS string); default 160
//   openSignal  — OPT-IN guided auto-open (S3g). A value the parent bumps to a NEW
//                 value to open this dropdown programmatically (used to flow through a
//                 pick sequence without re-clicking). Undefined/absent → never fires, so
//                 every consumer that doesn't pass it behaves byte-for-byte as before.
//
// CascadeSelect props:
//   levels      — [{ options, value, onChange, placeholder?, disabled?, allowClear?, ariaLabel?, width?, openSignal? }]
//                 one entry per dropdown, in order; each already filtered by the
//                 previous selection by the consumer.
//   gap         — px gap between dropdowns (default 6)
//   wrap        — bool; when false the dropdowns stay on ONE line (flex nowrap) instead
//                 of wrapping (default true = today's behavior, unchanged for consumers).

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
// The auto-advance decision lives in its own dependency-free module so `npm run check`
// can test it without React — it is the line that has broken three times.
import { shouldOpenFromSignal } from './cascade-signal.js';
import { DROPDOWN } from '../icons.js';
import { CELL_FONT } from './cellStyles.js';

// Font = CELL_FONT: in a grid cell, the cell's own (its column's text kind); anywhere
// else, the fs-small it always had (Alex, 2026-10-15: Edit mode matches read).
const btnBase = {
  ...CELL_FONT,
  padding: '2px 6px',
  border: 'var(--tg-filter-border)',
  borderRadius: 3,
  background: 'var(--th-row-even)',
  color: 'var(--th-text-body)',
  cursor: 'pointer',
  textAlign: 'left',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 4,
  boxSizing: 'border-box',
};

// Cascade consistency (2026-07-08): the closed button is content-width, but an
// UNSELECTED level used to size to its full placeholder text ('— any —',
// '— pick parent —', '— pick a placeable product —' …), making empty
// dropdowns much wider than a selected one (which shows just a short code).
// Cap the empty (unselected) state so it reads as ~2-3 chars; the full
// placeholder still lives in the button's `title` tooltip. Selected values
// stay content-width (codes are already short: 05, PE, RM) — untouched.
const EMPTY_MAX_W = 34;

const btnDisabled = {
  ...btnBase,
  background: 'var(--th-surface-disabled)',
  color: 'var(--th-text-disabled)',
  cursor: 'default',
};

// Popover base — rendered in a PORTAL on document.body with FIXED positioning,
// so it can never be clipped by a table cell's overflow:hidden (grids) or any
// scroll container. Position is computed from the trigger button's rect on open.
const popStyle = {
  position: 'fixed',
  background: 'var(--th-row-even)',
  border: 'var(--tg-filter-border)',
  borderRadius: 3,
  zIndex: 300,
  overflowY: 'auto',
  boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
};

const POP_MAX_H = 240;

// Compute the fixed-position style for the popover from the trigger's rect.
// Opens downward by default; flips upward when there's more room above.
// listWidth: the intended popover list width (the `width` prop) — the button
// itself is content-width when closed (F-uismalls), so the popover's readable
// width comes from this, not from the (now narrow) button rect.
function popPosition(btnRect, listWidth) {
  const below = window.innerHeight - btnRect.bottom - 8;
  const above = btnRect.top - 8;
  const openUp = below < 160 && above > below;
  const maxHeight = Math.min(POP_MAX_H, Math.max(120, openUp ? above : below));
  return {
    left: btnRect.left,
    minWidth: Math.max(btnRect.width, listWidth || 0),
    maxHeight,
    ...(openUp
      ? { bottom: window.innerHeight - btnRect.top + 2 }
      : { top: btnRect.bottom + 2 }),
  };
}

const rowStyle = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  gap: 8,
  alignItems: 'baseline',
  padding: '3px 8px',
  cursor: 'pointer',
  borderBottom: '1px solid var(--th-border-light)',
  whiteSpace: 'nowrap',
};

const codeCellStyle = {
  fontFamily: 'var(--ff-mono)',
  fontSize: 'var(--fs-mono)',
  fontWeight: 600,
  color: 'var(--th-navy)',
};

// Closed-button code text — SAME as codeCellStyle but at the button's own size,
// not the popover list's fs-mono. Without this, a selected level's code rendered 2px
// taller than an unpicked level's placeholder (both live in the same btnBase, but only
// the selected one carried its own fontSize), so a row of partly-filled cascades was
// visibly uneven height for a reason that had nothing to do with any OTHER field type.
const codeButtonStyle = { ...codeCellStyle, fontSize: CELL_FONT.fontSize };

const labelCellStyle = {
  fontSize: 'var(--fs-small)',
  color: 'var(--th-text-body)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const hintCellStyle = {
  fontSize: 'var(--fs-small)',
  color: 'var(--th-text-secondary)',
  fontVariantNumeric: 'tabular-nums',
};

export function CascadeDropdown({
  value,
  onChange,
  options = [],
  placeholder = '— any —',
  disabled = false,
  allowClear = true,
  ariaLabel,
  width = 160,
  openSignal, // S3g — opt-in guided auto-open (see props doc above)
}) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [popPos, setPopPos] = useState(null); // fixed-position style, computed on open
  const rootRef = useRef(null);
  const listRef = useRef(null);

  const cur = value == null ? '' : String(value);
  const selected = options.find(o => String(o.code) === cur) || null;

  // The full navigable row set (a leading clear row when allowClear).
  const rows = allowClear
    ? [{ code: '', label: placeholder, _clear: true }, ...options]
    : options;

  const close = useCallback(() => { setOpen(false); setActiveIdx(-1); setPopPos(null); }, []);

  const toggleOpen = useCallback(() => {
    setOpen(o => {
      if (o) { setActiveIdx(-1); setPopPos(null); return false; }
      const btn = rootRef.current?.querySelector('button');
      if (btn) setPopPos(popPosition(btn.getBoundingClientRect(), width));
      return true;
    });
  }, []);

  // Click-outside dismissal — the list lives in a body portal, so check BOTH refs.
  // Also close on any outside scroll/resize (the fixed position would go stale);
  // scrolling INSIDE the option list keeps it open.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      close();
    };
    const onScroll = (e) => { if (listRef.current && listRef.current.contains(e.target)) return; close(); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  // On open, point the active row at the current selection.
  useEffect(() => {
    if (!open) return;
    const idx = rows.findIndex(r => String(r.code) === cur);
    setActiveIdx(idx >= 0 ? idx : 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the active row scrolled into view.
  useEffect(() => {
    if (!open || activeIdx < 0 || !listRef.current) return;
    const el = listRef.current.children[activeIdx];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, activeIdx]);

  // S3g — opt-in guided auto-open. When `openSignal` changes to a NEW value, open this
  // dropdown programmatically (the parent bumps it to advance a pick flow, e.g.
  // Source→L1→L2→L3→item→Size, so the user flows through without re-clicking). A
  // last-handled ref means a given signal opens EXACTLY ONCE — re-renders, Escape, or
  // clicking away never re-open it (the parent only bumps on a real pick). Absent
  // (undefined) → this effect no-ops, so non-passing consumers are unchanged.
  // ⚠ THE BASELINE IS `undefined`, NOT THE PROP AT MOUNT — and the history matters,
  // because both versions of this line have shipped a bug.
  //
  // 2026-08-03: the ref started at a literal `null` while PathDropdowns passed a
  // counter starting at 0. null !== 0, so the first render looked like a bump and the
  // Parent cascade opened itself the moment its row entered edit mode. The fix then was
  // to seed FROM the prop, which made "mounted holding 0" indistinguishable from
  // "already handled" — correct, but only because TWO conventions existed at once.
  //
  // 2026-08-04: useCascadeSignal made the convention ONE — a signal is `undefined`
  // unless this control is the one being targeted right now. Under that rule, seeding
  // from the prop is exactly wrong for a control that MOUNTS LATE: the catalog item
  // picker only appears once search results arrive, i.e. AFTER the L3 pick bumped its
  // signal, so it mounted with the bump already in its baseline and never opened.
  // Alex found it immediately: "adding a component in Product does not Auto cascade".
  //
  // With one convention, a defined signal at mount MEANS "you are the target" — so the
  // baseline is `undefined` and mounting into a live signal opens, which is the whole
  // point. The 2026-08-03 regression cannot return, because nothing produces a signal
  // that is a number at mount any more (useCascadeSignal starts every key undefined,
  // and check:cascade enforces that no consumer hand-rolls one).
  const lastOpenSignal = useRef(undefined);
  useEffect(() => {
    if (!shouldOpenFromSignal(lastOpenSignal.current, openSignal)) return;
    lastOpenSignal.current = openSignal;
    if (disabled) return;
    const btn = rootRef.current?.querySelector('button');
    if (!btn) return;
    setPopPos(popPosition(btn.getBoundingClientRect(), width));
    setOpen(true);
  }, [openSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (row) => { onChange(row._clear ? '' : row.code); close(); };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); toggleOpen(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(rows.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActiveIdx(0); }
    else if (e.key === 'End') { e.preventDefault(); setActiveIdx(rows.length - 1); }
    else if (e.key === 'Enter') { e.preventDefault(); if (rows[activeIdx]) pick(rows[activeIdx]); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Tab') { close(); }
  };

  // Tag/code text for the closed button. Falls back through display → code → label
  // so an option with no code (empty tag) still shows something instead of blank
  // (found 2026-07-02: product leaves with an empty `code` rendered an empty button).
  const selDisplay = selected
    ? (selected.display || selected.code || selected.label || '')
    : '';

  // Tooltip code text — `display ?? code`, the SAME fallback the option list
  // uses when rendering the mono code column. Before this the tooltip printed
  // the raw `code`, so an option whose `display` deliberately hides an internal
  // value (the Size cell's `#NA` sentinel, 2026-07-21) leaked that value on
  // hover even though the button itself read correctly.
  const selectedCodeText = selected
    ? (selected.display != null ? selected.display : selected.code)
    : '';

  // F-uismalls: the closed button is content-width (fits the tag text only) —
  // `width` now only sizes the open popover's list (via popPosition), not the
  // closed button. inline-flex on the wrapper lets it shrink to the button.
  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); if (!disabled) toggleOpen(); }}
        onKeyDown={onKeyDown}
        style={{ ...(disabled ? btnDisabled : btnBase), width: 'fit-content', ...(selected ? null : { maxWidth: EMPTY_MAX_W }) }}
        title={selected ? `${selectedCodeText} ${selected.label}` : placeholder}
      >
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: selected ? 'var(--th-text-body)' : 'var(--th-text-disabled)',
        }}>
          {/* A6 (overnight batch): closed button shows the TAG/code only (mono);
              the label stays available in the button's title tooltip. */}
          {selected
            ? <span style={codeButtonStyle}>{selDisplay}</span>
            : placeholder}
        </span>
        <span aria-hidden="true" style={{ color: 'var(--th-text-secondary)', flexShrink: 0 }}>{DROPDOWN}</span>
      </button>

      {open && !disabled && popPos && createPortal(
        <div style={{ ...popStyle, ...popPos }} role="listbox" ref={listRef} onClick={(e) => e.stopPropagation()}>
          {rows.length === 0 && (
            <div style={{ ...rowStyle, cursor: 'default', gridTemplateColumns: '1fr', color: 'var(--th-text-disabled)' }}>
              No options
            </div>
          )}
          {rows.map((r, i) => {
            const isActive = i === activeIdx;
            const isSel = String(r.code) === cur;
            return (
              <div
                key={r._clear ? '__clear__' : String(r.code)}
                role="option"
                aria-selected={isSel}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => pick(r)}
                style={{
                  ...rowStyle,
                  background: isActive ? 'var(--th-row-hover)' : (isSel ? 'var(--th-row-selected)' : 'transparent'),
                }}
              >
                {r._clear ? (
                  <span style={{ ...labelCellStyle, gridColumn: '1 / -1', color: 'var(--th-text-secondary)' }}>{r.label}</span>
                ) : (
                  <>
                    <span style={codeCellStyle}>{r.display != null ? r.display : r.code}</span>
                    <span style={labelCellStyle}>{r.label}</span>
                    {r.hint != null && <span style={hintCellStyle}>{r.hint}</span>}
                  </>
                )}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

// useCascadeSignal — THE auto-advance plumbing, in one place (2026-08-04).
//
// Alex, 2026-08-03: "Can we build a specific REACT or something to make this set in
// stone. We must have all cases now to do that well." This is that, and it is a hook
// rather than a new component because the ATOM was never the problem -- CascadeDropdown
// is shared and works. What was duplicated is the six lines of signal plumbing every
// cascade wrote for itself, and an inventory of the codebase found FIVE hand-rolled
// copies (ProductsView's component editor, OnshapePanel's RegisterForm, PathDropdowns,
// ProjectsView's swap, LevelCascade) plus SEVEN cascade sites with no auto-advance at
// all -- against a rule Alex has stated as part of the definition of the word:
//
//   "AUTO-ADVANCE IS PART OF THE DEFINITION. Picking a level MUST open the next.
//    A cascade that makes him click each level open is an incomplete implementation,
//    not a variant."
//
// Usage: `const cascade = useCascadeSignal()` then, per level,
//   openSignal={cascade.signalFor('l2')}   and, in that level's onChange,
//   cascade.advance('l3')                  -- name the level to OPEN, not the one picked.
//
// ⚠ THE CONVENTION IS UNDEFINED-UNTIL-TARGETED, deliberately. A signal is `undefined`
// for every level except the one being opened right now. The alternative -- a counter
// per level that always holds a number, starting at 0 -- is what PathDropdowns used, and
// it is how the "opened as soon as Edit" regression happened: a control that mounts
// holding a real value cannot be told apart from one that was just bumped, so it opens
// itself. Undefined-at-mount cannot do that. Both styles still work in the atom (see
// lastOpenSignal there); this hook exists so nobody has to choose again.
export function useCascadeSignal() {
  const [tick, setTick] = useState({ key: null, n: 0 });
  const advance = useCallback((key) => setTick(t => ({ key, n: t.n + 1 })), []);
  const signalFor = useCallback((key) => (tick.key === key ? tick.n : undefined), [tick]);
  // Nothing is targeted — used when a Cancel/reset must not leave a level primed to open.
  const reset = useCallback(() => setTick(t => ({ key: null, n: t.n })), []);
  return { advance, signalFor, reset };
}

export default function CascadeSelect({ levels = [], gap = 6, wrap = true }) {
  return (
    <div style={{ display: 'flex', gap, alignItems: 'flex-end', flexWrap: wrap ? 'wrap' : 'nowrap' }}>
      {levels.map((lvl, i) => (
        <CascadeDropdown
          key={i}
          value={lvl.value}
          onChange={lvl.onChange}
          options={lvl.options || []}
          placeholder={lvl.placeholder}
          disabled={lvl.disabled}
          allowClear={lvl.allowClear}
          ariaLabel={lvl.ariaLabel}
          width={lvl.width}
          openSignal={lvl.openSignal}
        />
      ))}
    </div>
  );
}
